import React from 'react';
import {
    FaBoxes,
    FaTrophy,
    FaSearch,
    FaDatabase,
    FaLaptopCode
} from 'react-icons/fa';

export const productMixMetadata = {
    id: 'product-mix',
    title: "Premium Services & Upsell Analysis",
    company: "PaperPlus Solutions",
    category: "Sales Analytics",
    cardDescription: "Uncover hidden revenue opportunities by analyzing product upsell rates. Find out which reps are maximizing deal value.",
    learningObjectives: [
        "Conditional Aggregation",
        "Cross-Sell Analysis",
        "Business Case Modeling",
        "ROI Calculation"
    ],
    scenario: (
        <div className="space-y-8">
            {/* Hero Section - Clean, Minimalist, Website Theme */}
            <div className="bg-white dark:bg-[#0f172a] rounded-xl border border-gray-100 dark:border-gray-800 p-8 shadow-sm relative overflow-hidden group hover:border-orange-500/20 transition-all duration-500">
                <div className="relative z-10">
                    <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4 tracking-tight">
                        PaperPlus <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-600 to-red-600">Upsell Analytics</span>
                    </h3>

                    <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-lg max-w-3xl font-light">
                        Marcus has a strategic question: <strong className="text-gray-900 dark:text-white font-semibold">"We offer three product lines - are we maximizing revenue per customer?"</strong>
                        {' '}Your mission is to analyze upsell performance, identify top performers, and quantify the ROI of a training program.
                    </p>
                </div>
                {/* Subtle Brand Accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-orange-500/5 to-red-500/5 rounded-full blur-2xl -mr-10 -mt-10"></div>
            </div>

            {/* Schema Grid - Product Mix Focus */}
            <div>
                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-4 pl-1">
                    Product Mix Columns (orders table)
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {[
                        { column: 'paper_units', desc: 'Base product (every order)' },
                        { column: 'printing_units', desc: 'Premium upsell (optional)' },
                        { column: 'delivery_units', desc: 'Convenience upsell (optional)' },
                        { column: 'total_amount_usd', desc: 'Combined order value' }
                    ].map((item) => (
                        <div key={item.column} className="bg-gray-50 dark:bg-[#162032] p-4 rounded-lg border border-gray-100 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-colors">
                            <div className="font-mono font-bold text-orange-600 dark:text-orange-400 text-sm mb-1">
                                {item.column}
                            </div>
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                                {item.desc}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    ),
    introduction: ``,
    hasSQL: true
};

export const productMixChallenges = [
    {
        id: 'product-mix-overview',
        title: 'Product Mix Baseline',
        question: `- From the \`orders\` table, count \`total_orders\`, \`with_printing\` (\`printing_units > 0\`), \`with_delivery\`, and \`with_both\`
- Return the adoption percentage for each upsell product`,
        keyConcepts: ['SUM()', 'CASE WHEN', 'conditional aggregation', 'COUNT()', 'percentage calculations'],
        defaultQuery: `-- TIP: COUNT(CASE WHEN condition THEN 1 END) counts only the matching rows
-- e.g. COUNT(CASE WHEN product_type = 'printing' THEN 1 END) AS printing_orders`,
        expectedColumns: [
            { name: 'total_orders', type: 'INTEGER' },
            { name: 'with_printing', type: 'INTEGER' },
            { name: 'with_delivery', type: 'INTEGER' },
            { name: 'with_both', type: 'INTEGER' },
            { name: 'printing_pct', type: 'DECIMAL' },
            { name: 'delivery_pct', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus wants to understand product adoption at a baseline level: how many orders include printing, delivery, or both? This data will inform where to focus the sales training investment.`,
        concept: `**Conditional Aggregation with CASE WHEN**

Wrap a condition inside \`SUM()\` to count only rows that meet it. Rows where the condition is false contribute 0 and are effectively ignored.

\`\`\`sql
SUM(CASE WHEN condition THEN 1 ELSE 0 END) AS count_name
\`\`\`

- Works inside any aggregate function: \`SUM\`, \`AVG\`, \`MAX\`.
- Combine multiple conditions with \`AND\` / \`OR\` inside \`CASE WHEN\`.
- Use \`100.0 * count / total\` for percentage (not \`100\`, to avoid integer division).`,
        context: `**The Business Request**

Marcus wants to understand baseline product adoption: How many orders include printing? Delivery? Both? This data will inform where to focus sales training.

**The Concept**

Conditional Aggregation uses \`CASE WHEN\` inside aggregate functions to count or sum only rows that meet specific conditions. This is the foundation of adoption metrics.

**The Syntax**

\`\`\`sql
SUM(CASE WHEN condition THEN 1 ELSE 0 END) AS count_name
\`\`\`

This counts rows where the condition is true (returns 1) and ignores rows where it's false (returns 0).

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

From the \`orders\` table, calculate:
1. Total orders
2. Orders with printing (\`printing_units > 0\`)
3. Orders with delivery (\`delivery_units > 0\`)
4. Orders with both
5. Percentage for printing and delivery adoption

</div>`,
        hint: `**Think about the structure:**

1. You need to count ALL orders first: \`COUNT(*)\`
2. For conditional counting, use: \`SUM(CASE WHEN condition THEN 1 ELSE 0 END)\`
3. The condition for printing orders: \`printing_units > 0\`
4. The condition for delivery orders: \`delivery_units > 0\`
5. For orders with BOTH: combine conditions with \`AND\`

**Pro tip:** Start by getting the total count and one conditional count working, then add the others!`,
        solution: `SELECT 
  COUNT(*) AS total_orders,
  SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END) AS with_printing,
  SUM(CASE WHEN delivery_units > 0 THEN 1 ELSE 0 END) AS with_delivery,
  SUM(CASE WHEN printing_units > 0 AND delivery_units > 0 THEN 1 ELSE 0 END) AS with_both,
  ROUND(100.0 * SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END) / COUNT(*), 1) AS printing_pct,
  ROUND(100.0 * SUM(CASE WHEN delivery_units > 0 THEN 1 ELSE 0 END) / COUNT(*), 1) AS delivery_pct
FROM orders`,
        tip: `CASE WHEN inside aggregates is powerful! Use it for conditional counting, conditional sums, and segmented metrics. This pattern appears constantly in analytics.`,
        explanation: `**Product Mix Analysis Complete!**

**What Just Happened:**

1. **COUNT(*)** counted all orders
2. **SUM(CASE WHEN...)** counted orders with each product
3. **Division** calculated adoption percentage
4. **ROUND** formatted to 1 decimal place

**How CASE WHEN in Aggregates Works:**

\`\`\`sql
SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END)
-- Returns: count of orders with printing
\`\`\`

**Business Insights:**

- **34% printing:** 66% missed this upsell opportunity
- **28% delivery:** Bundling opportunity with printing
- **High paper, low printing:** Training gap on premium services

**Pro Tip:** Use \`100.0\` (not \`100\`) for floating-point division. Integer division truncates: 3/5 = 0, but 3.0/5 = 0.6.`,
        concepts: [
            {
                icon: <FaLaptopCode className="text-3xl text-[#2563eb]" />,
                title: 'CASE WHEN Aggregation',
                description: 'Use CASE WHEN inside SUM() to count rows meeting specific conditions. Pattern: SUM(CASE WHEN condition THEN 1 ELSE 0 END) acts as a conditional counter.'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#f97316]" />,
                title: 'Percentage Calculations',
                description: 'Calculate percentages using (numerator / denominator) × 100. Always use 100.0 (not 100) for floating-point division to avoid integer truncation.'
            },
            {
                icon: <FaDatabase className="text-3xl text-[#10b981]" />,
                title: 'Product Adoption Metrics',
                description: 'Measure feature adoption by counting orders with specific characteristics. Essential for understanding upsell performance and identifying missed opportunities.'
            }
        ]
    },
    {
        id: 'rep-upsell-performance',
        title: 'Sales Rep Upsell Leaderboard',
        question: `- Filter to reps with at least 20 orders; calculate \`upsell_rate\` as printing orders as a % of total
- Return \`sales_rep_name\`, \`total_orders\`, \`printing_orders\`, and \`upsell_rate\`, ordered highest to lowest - top 10 only`,
        keyConcepts: ['INNER JOIN', 'GROUP BY', 'SUM()', 'COUNT()', 'CASE WHEN', 'percentage calculation', 'ORDER BY'],
        defaultQuery: `-- TIP: Upsell rate = printing orders / total orders per rep
-- Use CASE WHEN inside COUNT(), then JOIN sales_reps for names`,
        expectedColumns: [
            { name: 'sales_rep_name', type: 'TEXT' },
            { name: 'total_orders', type: 'INTEGER' },
            { name: 'printing_orders', type: 'INTEGER' },
            { name: 'upsell_rate', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus wants to identify the top-performing reps by printing upsell rate so he can study their techniques and replicate their success across the whole team.`,
        concept: `**HAVING for Post-Aggregation Filtering**

\`HAVING\` filters groups after \`GROUP BY\` has collapsed them. Unlike \`WHERE\`, which runs before aggregation, \`HAVING\` can reference aggregate results.

\`\`\`sql
GROUP BY rep_name
HAVING COUNT(*) >= 20   -- runs after grouping
\`\`\`

- \`WHERE\` filters individual rows: \`WHERE industry = 'Tech'\`.
- \`HAVING\` filters aggregated groups: \`HAVING COUNT(*) >= 20\`.
- Filtering for sample size ensures reported rates are statistically meaningful.`,
        context: `**The Business Request**

Marcus wants to identify top-performing reps so he can study their techniques and replicate their success across the team.

**The Concept**

This query combines multi-table JOINs with conditional aggregation. You need to:

1. Chain tables: \`orders\` → \`accounts\` → \`sales_reps\`
2. Calculate upsell rate per rep using CASE WHEN
3. Filter with HAVING for statistical significance

**The Syntax**

\`\`\`sql
SELECT rep_name, COUNT(*) AS total,
  ROUND(100.0 * SUM(CASE WHEN condition THEN 1 ELSE 0 END) / COUNT(*), 1) AS rate
FROM orders
JOIN accounts ON ...
JOIN sales_reps ON ...
GROUP BY rep_name
HAVING COUNT(*) >= 20
ORDER BY rate DESC
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

For each sales rep, show total orders, printing orders, and upsell rate (%). Only include reps with ≥20 orders. Order by rate DESC, limit to top 10.

</div>`,
        hint: `**Step-by-step approach:**

1. Start with the \`orders\` table
2. JOIN to \`accounts\` (to get sales_rep_id)
3. JOIN to \`sales_reps\` (to get rep names)
4. GROUP BY rep name
5. Use CASE WHEN to count printing orders
6. Calculate percentage: \`(printing_orders / total_orders) × 100\`
7. Filter with HAVING for reps with >= 20 orders
8. Sort by upsell rate descending

**Remember:** HAVING filters AFTER grouping, WHERE filters BEFORE!`,
        solution: `SELECT 
  sales_reps.sales_rep_name,
  COUNT(*) AS total_orders,
  SUM(CASE WHEN orders.printing_units > 0 THEN 1 ELSE 0 END) AS printing_orders,
  ROUND(100.0 * SUM(CASE WHEN orders.printing_units > 0 THEN 1 ELSE 0 END) / COUNT(*), 1) AS upsell_rate
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
GROUP BY sales_reps.sales_rep_name
HAVING COUNT(*) >= 20
ORDER BY upsell_rate DESC
LIMIT 10`,
        tip: `Always filter for statistical significance with HAVING COUNT(*) >= N. A rep with 3/5 orders (60%) upsold looks better than one with 50/100 (50%), but the latter is more reliable.`,
        explanation: `**Upsell Stars Identified!**

**What Just Happened:**

1. **FROM orders JOIN accounts JOIN sales_reps:** Combined all data
2. **GROUP BY sales_rep_name:** Grouped by rep
3. **COUNT + SUM(CASE...):** Calculated rate
4. **HAVING COUNT(*) >= 20:** Ensured statistical significance
5. **ORDER BY + LIMIT:** Showed top 10

**How HAVING Works:**

- **WHERE:** Filters *before* grouping (e.g., \`WHERE industry = 'Tech'\`)
- **HAVING:** Filters *after* aggregation (e.g., \`HAVING COUNT(*) >= 20\`)

**Business Impact:**

Top performers at 65% show what's possible:
- Interview them for best practices
- Record their sales calls
- Replicate techniques company-wide

**Statistical Significance Matters:**

- **Rep A (5 orders, 60%):** ❌ Too few orders to be reliable
- **Rep B (100 orders, 50%):** ✅ Reliable sample size`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'INNER JOIN',
                description: 'Connects multiple tables using matching keys. Chain JOINs to link orders → accounts → sales_reps for comprehensive analysis.'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#f97316]" />,
                title: 'GROUP BY with Aggregates',
                description: 'GROUP BY collapses rows into groups, then aggregates (COUNT, SUM, AVG) calculate metrics per group. Essential for per-rep or per-region analysis.'
            },
            {
                icon: <FaSearch className="text-3xl text-[#10b981]" />,
                title: 'HAVING Clause',
                description: 'Filters groups AFTER aggregation. Use HAVING for "reps with >= 20 orders" or "accounts with 0 upsells". WHERE filters before grouping.'
            }
        ]
    },
    {
        id: 'regional-upsell-analysis',
        title: 'Regional Upsell Performance Comparison',
        question: `- Join across all 4 tables and group by region
- Return \`region_name\`, \`total_orders\`, \`printing_upsell_rate\`, \`delivery_upsell_rate\`, and \`avg_order_value\`, ordered by printing upsell rate descending`,
        keyConcepts: ['INNER JOIN', '4 tables', 'GROUP BY', 'COUNT()', 'SUM()', 'AVG()', 'ROUND()'],
        defaultQuery: `-- TIP: Chain JOINs: orders -> accounts -> regions to reach region_name
-- Use HAVING to filter groups after aggregation`,
        expectedColumns: [
            { name: 'region_name', type: 'TEXT' },
            { name: 'total_orders', type: 'INTEGER' },
            { name: 'printing_upsell_rate', type: 'DECIMAL' },
            { name: 'delivery_upsell_rate', type: 'DECIMAL' },
            { name: 'avg_order_value', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus wants to compare upsell adoption across all four regions to spot which territories excel at selling premium services and where training is most needed.`,
        concept: `**Multi-Table JOIN with Multiple Conditional Aggregates**

Chain four tables then calculate several metrics in one \`SELECT\`. Each metric reuses the same \`CASE WHEN\` pattern against a different condition.

\`\`\`sql
SELECT region_name, COUNT(*) AS total,
  ROUND(100.0 * SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END) / COUNT(*), 1) AS print_rate,
  ROUND(AVG(total_amount_usd), 2) AS avg_value
FROM orders
JOIN accounts ON ... JOIN sales_reps ON ... JOIN regions ON ...
GROUP BY region_name
\`\`\`

- Build and confirm JOINs first, then layer in each aggregate.
- Always show \`total_orders\` alongside any rate so readers can judge statistical weight.`,
        context: `**The Business Request**

Marcus wants to compare regional performance to identify best practices and training needs. Are some territories naturally better at selling premium services?

**The Concept**

This requires chaining 4 tables and calculating multiple metrics in one query:

- **JOIN Chain:** orders → accounts → sales_reps → regions
- **Multiple Aggregates:** COUNT, AVG, and CASE WHEN percentages

**The Syntax**

\`\`\`sql
SELECT region_name, COUNT(*) AS total,
  ROUND(100.0 * SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END) / COUNT(*), 1) AS print_rate,
  ROUND(AVG(total_amount_usd), 2) AS avg_value
FROM orders
JOIN accounts ON ... JOIN sales_reps ON ... JOIN regions ON ...
GROUP BY region_name
ORDER BY print_rate DESC
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Show each region's: \`region_name\`, \`total_orders\`, \`printing_upsell_rate\`, \`delivery_upsell_rate\`, and \`avg_order_value\`. Order by printing rate DESC.

</div>`,
        hint: `**Think about the JOIN chain:**

orders → accounts → sales_reps → regions

Each JOIN connects through a foreign key. Once you have all 4 tables connected:
- GROUP BY region name
- Calculate upsell rates using CASE WHEN (like previous challenge)
- Calculate AVG of order amounts
- Sort by printing upsell rate

**Tip:** Build the JOINs first, then add the aggregations!`,
        solution: `SELECT 
  regions.region_name,
  COUNT(*) AS total_orders,
  ROUND(100.0 * SUM(CASE WHEN orders.printing_units > 0 THEN 1 ELSE 0 END) / COUNT(*), 1) AS printing_upsell_rate,
  ROUND(100.0 * SUM(CASE WHEN orders.delivery_units > 0 THEN 1 ELSE 0 END) / COUNT(*), 1) AS delivery_upsell_rate,
  ROUND(AVG(orders.total_amount_usd), 2) AS avg_order_value
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
INNER JOIN regions ON sales_reps.region_id = regions.region_id
GROUP BY regions.region_name
ORDER BY printing_upsell_rate DESC`,
        tip: `When comparing percentages across groups, always show denominators (total_orders). A 50% rate with 1,000 orders is more meaningful than 80% with 10 orders.`,
        explanation: `**Regional Patterns Revealed!**

**What Just Happened:**

1. **SUM orders:** Volume baseline
2. **Printing upsell %:** Feature adoption rate
3. **Delivery upsell %:** Bundling success rate
4. **AVG units:** Customer size indicator

**How to Compare Regions:**

- **North (42%):** Best practice leader
- **South (35%):** Above average
- **East (30%):** Improvement opportunity
- **West (26%):** Needs training intervention

**Business Impact:**

That 16-point gap (North vs West) represents millions in potential revenue:
- Share North's techniques with West
- Identify what's different (training? customers? products?)
- Set regional improvement goals

**Pro Tip:** Always show denominators (total_orders). A 50% rate with 1,000 orders is more meaningful than 80% with 10 orders.`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'Multi-Table JOINs',
                description: 'Chain 4 tables (orders → accounts → sales_reps → regions) to connect all related data. Each JOIN uses ON to match foreign keys.'
            },
            {
                icon: <FaLaptopCode className="text-3xl text-[#f97316]" />,
                title: 'Multiple Metrics',
                description: 'Calculate several aggregates in one query: COUNT for volume, percentages for rates, AVG for averages. Provides comprehensive regional comparison.'
            },
            {
                icon: <FaTrophy className="text-3xl text-[#10b981]" />,
                title: 'Regional Analysis',
                description: 'Compare performance across geographic regions to identify best practices, training needs, and market opportunities.'
            }
        ]
    },
    {
        id: 'cross-sell-opportunities',
        title: 'Cross-Sell Opportunity Identification',
        question: `- Find accounts with 5+ total orders that have **never** purchased printing services
- Return \`account_name\`, \`sales_rep_name\`, \`total_orders\`, and \`total_revenue\`, ordered by revenue descending`,
        keyConcepts: ['GROUP BY', 'COUNT()', 'SUM()', 'CASE WHEN', 'HAVING', 'ORDER BY'],
        defaultQuery: `-- TIP: LEFT JOIN + IS NULL finds accounts that never appear in orders
-- FROM accounts LEFT JOIN orders ON ... WHERE orders.account_id IS NULL`,
        expectedColumns: [
            { name: 'account_name', type: 'TEXT' },
            { name: 'sales_rep_name', type: 'TEXT' },
            { name: 'total_orders', type: 'INTEGER' },
            { name: 'total_revenue', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus wants a targeted outreach list: accounts with 5+ orders that have never purchased printing services. These warm leads have the highest upsell success rate.`,
        concept: `**HAVING with Multiple Conditions**

Chain conditions in \`HAVING\` with \`AND\` to isolate groups that simultaneously meet multiple criteria.

\`\`\`sql
GROUP BY account_name
HAVING COUNT(*) >= 5
   AND SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END) = 0
\`\`\`

- First condition filters for engaged accounts (high order volume).
- Second condition filters for zero printing conversion.
- Ordering by revenue puts highest-value targets first for the sales team.`,
        context: `**The Business Request**

Marcus wants to build a targeted outreach list: accounts that are loyal customers (frequent orders) but have never purchased printing services. These are warm leads for upselling.

**The Concept**

This query uses HAVING with multiple conditions to find accounts that meet TWO criteria:

1. High engagement (≥5 total orders)
2. Zero printing adoption

**The Syntax**

\`\`\`sql
SELECT account_name, COUNT(*) AS orders, SUM(revenue) AS total
FROM orders
JOIN accounts ON ...
GROUP BY account_name
HAVING COUNT(*) >= 5 
   AND SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END) = 0
ORDER BY total DESC
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Find accounts with ≥5 orders but ZERO printing orders. Show account_name, sales_rep_name, total_orders, and total_revenue. Order by revenue DESC.

</div>`,
        hint: `**The key insight:**

You need TWO conditions in HAVING:
1. Total orders >= 5
2. Printing orders = 0 (use SUM(CASE WHEN...))

**Structure:**
- JOIN orders → accounts → sales_reps
- GROUP BY account and rep
- HAVING with AND to combine both conditions
- ORDER BY revenue to prioritize high-value accounts

**Think:** How do you count orders where printing_units = 0?`,
        solution: `SELECT 
  accounts.account_name,
  sales_reps.sales_rep_name,
  COUNT(*) AS total_orders,
  SUM(orders.total_amount_usd) AS total_revenue
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
GROUP BY accounts.account_name, sales_reps.sales_rep_name
HAVING 
  COUNT(*) >= 5 
  AND SUM(CASE WHEN orders.printing_units > 0 THEN 1 ELSE 0 END) = 0
ORDER BY total_revenue DESC`,
        tip: `This "find high-value customers NOT using feature X" query is one of the most valuable in SaaS/B2B analytics. It directly drives revenue by identifying warm leads.`,
        explanation: `**Upsell Goldmine Discovered!**

**What Just Happened:**

1. **GROUP BY account_id:** Aggregate per account
2. **HAVING COUNT(*) >= 5:** Loyal customers (high engagement)
3. **HAVING SUM(..printing..) = 0:** Never purchased printing
4. **ORDER BY revenue DESC:** Prioritize by value

**How HAVING with Multiple Conditions Works:**

\`\`\`sql
HAVING COUNT(*) >= 5              -- Condition 1: Engaged
   AND SUM(..printing..) = 0      -- Condition 2: Not converted
\`\`\`

**Revenue Impact:**

- **Target accounts:** 50
- **Conversion rate:** 30% (estimate) -> 15 accounts
- **Avg orders/year:** 10
- **Printing value/order:** $500
- **Annual opportunity:** **$75,000**

**This is the "Engaged but Not Converted" segment** - highest upsell success rate because they already trust you.`,
        concepts: [
            {
                icon: <FaSearch className="text-3xl text-[#2563eb]" />,
                title: 'HAVING with Multiple Conditions',
                description: 'Combine multiple HAVING conditions with AND/OR. Filter for "accounts with >= 5 orders AND 0 printing orders" to find specific segments.'
            },
            {
                icon: <FaTrophy className="text-3xl text-[#f97316]" />,
                title: 'Opportunity Identification',
                description: 'Find high-value customers NOT using a feature. This "engaged but not converted" segment has the highest upsell success rate.'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#10b981]" />,
                title: 'Prioritization by Value',
                description: 'Sort by revenue to prioritize high-value accounts. Focus sales efforts where they generate the most impact.'
            }
        ]
    },
    {
        id: 'product-line-revenue',
        title: 'Product Line Revenue Breakdown',
        question: `- Use the pricing structure: paper = $10/unit, printing = $50/unit, delivery = $25/unit
- Return \`paper_revenue\`, \`printing_revenue\`, \`delivery_revenue\`, \`total_revenue\`, and each product line's % share of total revenue`,
        keyConcepts: ['SUM()', 'multiple columns', 'percentage calculation', 'ROUND()', 'single-row result'],
        defaultQuery: `-- TIP: Use CASE WHEN to apply a different price per product type
-- SUM(CASE WHEN product_type = 'paper' THEN quantity * 10 ELSE 0 END)`,
        expectedColumns: [
            { name: 'paper_revenue', type: 'DECIMAL' },
            { name: 'printing_revenue', type: 'DECIMAL' },
            { name: 'delivery_revenue', type: 'DECIMAL' },
            { name: 'total_revenue', type: 'DECIMAL' },
            { name: 'paper_pct', type: 'DECIMAL' },
            { name: 'printing_pct', type: 'DECIMAL' },
            { name: 'delivery_pct', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus needs to understand revenue composition by product line to allocate commissions fairly and build the business case for investing in upsell training.`,
        concept: `**Calculated Revenue Columns**

Multiply unit quantities by a fixed price inside \`SUM()\` to derive revenue when no pre-calculated revenue column exists.

\`\`\`sql
SUM(paper_units * 10) AS paper_revenue,
SUM(printing_units * 50 + delivery_units * 25) AS upsell_revenue,
ROUND(100.0 * SUM(printing_units * 50) / SUM(all_revenue), 1) AS printing_pct
FROM orders
\`\`\`

- Use \`100.0\` (not \`100\`) to force floating-point division: \`3 / 5 = 0\`, but \`3.0 / 5 = 0.6\`.
- All calculations run in a single \`SELECT\` from \`orders\` - no JOIN or GROUP BY required.`,
        context: `**The Business Request**

Marcus needs to understand the revenue composition by product line to allocate commissions fairly and justify training investments.

**The Concept**

Revenue calculations multiply unit quantities by prices, then calculate each product's percentage of total.

**Pricing:**

- Paper: $10/unit
- Printing: $50/unit (higher margin)
- Delivery: $25/unit

**The Syntax**

\`\`\`sql
SELECT 
  SUM(paper_units * 10) AS paper_revenue,
  SUM(printing_units * 50) AS printing_revenue,
  ROUND(100.0 * SUM(printing_units * 50) / SUM(total), 1) AS printing_pct
FROM orders
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Calculate: \`paper_revenue\`, \`printing_revenue\`, \`delivery_revenue\`, \`total_revenue\`, and the percentage for each product line.

</div>`,
        hint: `**Revenue calculation pattern:**

1. Calculate revenue for each product line:
   - Paper: \`SUM(paper_units * 10)\`
   - Printing: \`SUM(printing_units * 50)\`
   - Delivery: \`SUM(delivery_units * 25)\`

2. Calculate total revenue (sum of all three)

3. Calculate percentages: \`(product_revenue / total_revenue) × 100\`

**Tip:** You can do all calculations in a single SELECT from the orders table!`,
        solution: `SELECT 
  SUM(paper_units * 10) AS paper_revenue,
  SUM(printing_units * 50) AS printing_revenue,
  SUM(delivery_units * 25) AS delivery_revenue,
  SUM(paper_units * 10 + printing_units * 50 + delivery_units * 25) AS total_revenue,
  ROUND(100.0 * SUM(paper_units * 10) / SUM(paper_units * 10 + printing_units * 50 + delivery_units * 25), 1) AS paper_pct,
  ROUND(100.0 * SUM(printing_units * 50) / SUM(paper_units * 10 + printing_units * 50 + delivery_units * 25), 1) AS printing_pct,
  ROUND(100.0 * SUM(delivery_units * 25) / SUM(paper_units * 10 + printing_units * 50 + delivery_units * 25), 1) AS delivery_pct
FROM orders`,
        tip: `Use 100.0 (not 100) for floating-point division. Integer division truncates: 3/5 = 0, but 3.0/5 = 0.6.`,
        explanation: `**Revenue Composition Revealed!**

**What Just Happened:**

1. **SUM(units * price):** Calculate revenue per product
2. **SUM(all products):** Calculate total revenue
3. **Product / Total * 100:** Calculate percentage

**How Revenue Calculations Work:**

\`\`\`sql
SUM(paper_units * 10) AS paper_revenue
-- Multiplies each order's units by price, then sums all orders
\`\`\`

**Product Revenue Breakdown:**

- **Paper (100% adoption):** 52% of revenue (Base product)
- **Printing (34% adoption):** 38% of revenue (High-margin upsell)
- **Delivery (28% adoption):** 10% of revenue (Convenience add-on)

**Business Impact:**

Printing drives 38% of revenue despite only 34% adoption. If adoption went from 34% to 50%:
- That's **$5.6M additional revenue** (47% increase!)

**Pro Tip:** Use \`100.0\` (not \`100\`) for floating-point division.`,
        concepts: [
            {
                icon: <FaLaptopCode className="text-3xl text-[#2563eb]" />,
                title: 'Revenue Calculations',
                description: 'Multiply unit quantities by prices to calculate revenue: SUM(units * price). Essential for financial analysis and forecasting.'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#f97316]" />,
                title: 'Product Mix Analysis',
                description: 'Break down total revenue by product line to understand revenue composition. Identifies which products drive profitability.'
            },
            {
                icon: <FaTrophy className="text-3xl text-[#10b981]" />,
                title: 'Percentage Composition',
                description: 'Show each product as % of total revenue. Reveals that high-margin products may drive more revenue despite lower adoption.'
            }
        ]
    },
    {
        id: 'top-upsellers-detailed',
        title: 'Elite Upseller Deep Dive',
        question: `- Rank reps by upsell revenue (printing + delivery combined); keep the top 5
- Return \`sales_rep_name\`, \`total_orders\`, \`base_revenue\` (paper only), \`upsell_revenue\`, \`total_revenue\`, and \`upsell_pct\``,
        keyConcepts: ['INNER JOIN', 'GROUP BY', 'SUM()', 'calculated columns', 'ORDER BY', 'LIMIT'],
        defaultQuery: `-- TIP: ORDER BY upsell_revenue DESC LIMIT 5 gives the top 5
-- JOIN sales_reps to orders to replace rep IDs with names`,
        expectedColumns: [
            { name: 'sales_rep_name', type: 'TEXT' },
            { name: 'total_orders', type: 'INTEGER' },
            { name: 'base_revenue', type: 'DECIMAL' },
            { name: 'upsell_revenue', type: 'DECIMAL' },
            { name: 'total_revenue', type: 'DECIMAL' },
            { name: 'upsell_pct', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus wants to rank reps by actual upsell dollars - not just percentage rate. A high-rate rep on small accounts may generate less revenue than a moderate-rate rep on large accounts.`,
        concept: `**Ranking by Revenue vs Rate**

Switch the \`ORDER BY\` from a percentage to an absolute amount to surface reps with the greatest business impact rather than the highest conversion rate.

\`\`\`sql
SUM(paper_units * 10) AS base_revenue,
SUM(printing_units * 50 + delivery_units * 25) AS upsell_revenue
ORDER BY upsell_revenue DESC   -- rank by dollars, not percentage
\`\`\`

- A 65% rate on 50 orders can generate less revenue than a 45% rate on 500 orders.
- Always report both rate (\`%\`) and absolute revenue (\`$\`) to give a complete picture.`,
        context: `**The Business Request**

Marcus wants to identify the top revenue-generating upsellers-not by rate, but by actual dollars. A rep with 50% rate on small accounts matters less than 40% on large accounts.

**The Concept**

This query separates base revenue (paper) from upsell revenue (printing + delivery) to see who's adding the most value.

**The Syntax**

\`\`\`sql
SELECT rep_name,
  SUM(paper_units * 10) AS base,
  SUM(printing_units * 50 + delivery_units * 25) AS upsell
FROM orders JOIN accounts ON ... JOIN sales_reps ON ...
GROUP BY rep_name
ORDER BY upsell DESC LIMIT 5
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Show top 5 reps by upsell revenue. Include: total_orders, base_revenue, upsell_revenue, total_revenue, and upsell_percentage.

</div>`,
        hint: `**Break down the revenue:**

1. Base revenue = paper only
2. Upsell revenue = printing + delivery
3. Total revenue = base + upsell
4. Upsell percentage = (upsell / total) × 100

**Structure:**
- JOIN orders → accounts → sales_reps
- GROUP BY rep name
- Calculate all revenue metrics in SELECT
- ORDER BY upsell_revenue (not rate!)

**Key difference:** This sorts by DOLLARS, not percentage!`,
        solution: `SELECT 
  sales_reps.sales_rep_name,
  COUNT(*) AS total_orders,
  SUM(orders.paper_units * 10) AS base_revenue,
  SUM(orders.printing_units * 50 + orders.delivery_units * 25) AS upsell_revenue,
  SUM(orders.paper_units * 10 + orders.printing_units * 50 + orders.delivery_units * 25) AS total_revenue,
  ROUND(100.0 * SUM(orders.printing_units * 50 + orders.delivery_units * 25) / 
        SUM(orders.paper_units * 10 + orders.printing_units * 50 + orders.delivery_units * 25), 1) AS upsell_pct
FROM orders
INNER JOIN accounts ON orders.account_id = accounts.account_id
INNER JOIN sales_reps ON accounts.sales_rep_id = sales_reps.sales_rep_id
GROUP BY sales_reps.sales_rep_name
ORDER BY upsell_revenue DESC
LIMIT 5`,
        tip: `Always look at multiple dimensions: rates AND absolute numbers. High rate × low volume = mediocre impact.`,
        explanation: `**Revenue Champions Identified!**

**What Just Happened:**

1. **Base revenue:** \`paper_units * $10\`
2. **Upsell revenue:** \`printing_units * $50 + delivery_units * $25\`
3. **Total revenue:** Base + Upsell
4. **ORDER BY upsell:** Rank by upsell contribution

**How Revenue vs Rate Analysis Works:**

- **Sarah:** 65% rate, 200 orders -> **$485K upsell revenue**
- **Mike:** 80% rate, 50 orders -> **$120K upsell revenue**

Sarah generates 4x more revenue despite lower rate - volume matters!

**Business Impact:**

If bottom 50% of reps reached 75% of top performers:
- **$2M+ additional annual upsell revenue**
- Clear training ROI justification

**Pro Tip:** Always look at both RATE (%) and REVENUE ($). High rate × low volume = mediocre impact.`,
        concepts: [
            {
                icon: <FaTrophy className="text-3xl text-[#2563eb]" />,
                title: 'Revenue vs Rate',
                description: 'Analyze both upsell RATE (%) and upsell REVENUE ($). High rate with low volume may generate less than moderate rate with high volume.'
            },
            {
                icon: <FaLaptopCode className="text-3xl text-[#f97316]" />,
                title: 'Calculated Revenue Columns',
                description: 'Create separate columns for base revenue, upsell revenue, and total revenue. Shows revenue composition and upsell contribution clearly.'
            },
            {
                icon: <FaBoxes className="text-3xl text-[#10b981]" />,
                title: 'Multi-Dimensional Metrics',
                description: 'Always analyze multiple dimensions: rates AND absolute numbers, counts AND percentages. Complete picture prevents misleading conclusions.'
            }
        ]
    },
    {
        id: 'upsell-program-roi',
        title: 'Upsell Training Program ROI Calculation',
        question: `- Build a CTE-based ROI model for a $50K training programme
- Assume a 10-point improvement in upsell rates to project additional revenue
- Return \`total_orders\`, \`current_upsell_revenue\`, \`additional_printing_revenue\`, \`additional_delivery_revenue\`, \`total_additional_revenue\`, and \`net_roi\` (after the $50K cost)`,
        keyConcepts: ['WITH', 'CTE', 'SUM()', 'CASE WHEN', 'mathematical calculations', 'scenario analysis'],
        defaultQuery: `-- TIP: WITH cte_name AS (...) creates a named temporary result
-- Chain two CTEs: one to aggregate revenue, one to calculate ROI`,
        expectedColumns: [
            { name: 'total_orders', type: 'INTEGER' },
            { name: 'current_upsell_revenue', type: 'DECIMAL' },
            { name: 'additional_printing_revenue', type: 'DECIMAL' },
            { name: 'additional_delivery_revenue', type: 'DECIMAL' },
            { name: 'total_additional_revenue', type: 'DECIMAL' },
            { name: 'net_roi', type: 'DECIMAL' },
        ],
        businessRequest: `Marcus needs to justify a $50K training programme to the CFO. He predicts a 10-point improvement in upsell rates and wants to translate that hypothesis into projected revenue and net ROI.`,
        concept: `**CTE for Scenario Modelling**

A \`WITH\` clause calculates the current baseline once, then the main query projects the "what if" scenario against those numbers.

\`\`\`sql
WITH current_state AS (
  SELECT COUNT(*) AS total_orders,
         SUM(printing_units * 50 + delivery_units * 25) AS current_upsell_revenue
  FROM orders
)
SELECT total_orders,
       current_upsell_revenue,
       (total_orders * 0.10 * 200 * 50) AS additional_printing_revenue
FROM current_state
\`\`\`

- Define the baseline in the CTE; reference its columns in the main \`SELECT\` for projections.
- Subtracting cost from projected gain produces a clean \`net_roi\` column for the business case.`,
        context: `**The Business Request**

Marcus needs to justify a $50K training program to the CFO. He hypothesizes that training can improve upsell rates by 10 percentage points.

**The Concept**

Use a CTE to calculate current state, then project the "what if" scenario in the main query. This pattern is essential for ROI analysis and business case modeling.

**The Syntax**

\`\`\`sql
WITH current_state AS (
  SELECT COUNT(*) AS total_orders,
    SUM(printing_units * 50 + delivery_units * 25) AS current_revenue
  FROM orders
)
SELECT total_orders, current_revenue,
  (total_orders * 0.10 * 200 * 50) AS additional_revenue
FROM current_state
\`\`\`

<div style="background: #f0f9ff; border-left: 4px solid #3b82f6; padding: 16px; margin: 16px 0; border-radius: 4px;">

**Your Task:**

Use a CTE to model: current upsell revenue, projected additional revenue (10% more orders × avg units × price), and net ROI after $50K training cost.

</div>`,
        hint: `**CTE Structure:**

1. Create a CTE called \`current_state\` that calculates:
   - Total orders
   - Current printing/delivery order counts
   - Current upsell revenue

2. In the main query, use those numbers to project:
   - If 10% more orders had printing (at avg 200 units × $50)
   - If 10% more orders had delivery (at avg 100 units × $25)
   - Subtract $50K training cost for net ROI

**Tip:** WITH clause goes before SELECT!`,
        solution: `WITH current_state AS (
  SELECT 
    COUNT(*) AS total_orders,
    SUM(CASE WHEN printing_units > 0 THEN 1 ELSE 0 END) AS current_printing_orders,
    SUM(CASE WHEN delivery_units > 0 THEN 1 ELSE 0 END) AS current_delivery_orders,
    SUM(printing_units * 50 + delivery_units * 25) AS current_upsell_revenue
  FROM orders
)
SELECT 
  total_orders,
  current_upsell_revenue,
  (total_orders * 0.10 * 200 * 50) AS additional_printing_revenue,
  (total_orders * 0.10 * 100 * 25) AS additional_delivery_revenue,
  (total_orders * 0.10 * 200 * 50) + (total_orders * 0.10 * 100 * 25) AS total_additional_revenue,
  ((total_orders * 0.10 * 200 * 50) + (total_orders * 0.10 * 100 * 25)) - 50000 AS net_roi
FROM current_state`,
        tip: `CTEs perfect for scenario analysis! Calculate "current state" then project "what if" scenarios.`,
        explanation: `**ROI Justified!**

**What Just Happened:**

| Step | CTE / Query | Purpose |
|------|-------------|--------|
| 1 | WITH current_state | Calculate baseline metrics |
| 2 | Main query | Project "what if" scenario |
| 3 | Delta calculation | Show improvement vs cost |

**How Scenario Modeling Works:**

\`\`\`sql
WITH current_state AS (SELECT current metrics...)
SELECT
  current_value,
  projected_value,
  projected_value - current_value - cost AS net_roi
FROM current_state
\`\`\`

**ROI Calculation (3,500 orders):**

| Metric | Value |
|--------|-------|
| Additional printing revenue | $3.5M |
| Additional delivery revenue | $0.875M |
| **Total additional revenue** | **$4.375M** |
| Training cost | $50K |
| **Net ROI** | **$4.325M (8,650% return!)** |

**Even Conservative Estimates Win:**

Half the improvement = $2.2M revenue = 43:1 ROI

🎉 **Case Complete!** You've mastered upsell analytics, opportunity identification, and business case modeling.`,
        concepts: [
            {
                icon: <FaDatabase className="text-3xl text-[#2563eb]" />,
                title: 'CTEs (WITH Clause)',
                description: 'Common Table Expressions create temporary named result sets. Perfect for breaking complex queries into readable steps and scenario analysis.'
            },
            {
                icon: <FaLaptopCode className="text-3xl text-[#f97316]" />,
                title: 'Scenario Modeling',
                description: 'Calculate "current state" in CTE, then model "what if" scenarios in main query. Essential for ROI calculations and strategic planning.'
            },
            {
                icon: <FaTrophy className="text-3xl text-[#10b981]" />,
                title: 'Business Case Analysis',
                description: 'Quantify impact of initiatives with data. Show projected revenue, costs, and net ROI to justify investments and drive decisions.'
            }
        ]
    }
];

export const productMixConcepts = [
    {
        icon: <FaBoxes className="text-3xl text-[#2563eb]" />,
        title: 'CASE WHEN + Aggregates',
        description: 'Use CASE WHEN inside SUM/COUNT for conditional aggregation. Perfect for adoption rates, conversion metrics, and segmented statistics. Essential for product analytics.'
    },
    {
        icon: <FaTrophy className="text-3xl text-[#f97316]" />,
        title: 'Percentage Calculations',
        description: 'Calculate percentages: (numerator / denominator) × 100. Use 100.0 (not 100) for floating-point division. Round with ROUND(value, decimals).'
    },
    {
        icon: <FaSearch className="text-3xl text-[#fb923c]" />,
        title: 'HAVING Clause',
        description: 'HAVING filters AFTER aggregation, unlike WHERE which filters BEFORE. Find "accounts with >5 orders" or "reps with 0 upsells". Essential for post-aggregation filtering.'
    },
    {
        icon: <FaDatabase className="text-3xl text-[#1e3a5f]" />,
        title: 'CTEs for Scenario Analysis',
        description: 'Calculate "current state" in CTE, model "what if" scenarios in main query. Perfect for ROI calculations, forecasting, and strategic planning.'
    },
    {
        icon: <FaLaptopCode className="text-3xl text-[#10b981]" />,
        title: 'Multi-Dimensional Metrics',
        description: 'Always analyze multiple dimensions: rates AND absolute numbers, counts AND percentages. Complete picture prevents misleading conclusions.'
    }
];
